import { type RefObject, useEffect } from 'react';

import type { TDealsExperimentGroup } from '../constants/dealsExperiment';
import { caScrollCpdArticle } from '../utils/analytics/cpdDeals/trackingCpdDeals';
import {
  getArticleScrollPercent,
  getReachedArticleScrollThresholds,
  type TArticleScrollThreshold,
} from '../utils/analytics/cpdDeals/getArticleScrollPercent';

interface IUseTrackCpdArticleScrollParams {
  enabled: boolean;
  postId: number | undefined;
  abGroup: TDealsExperimentGroup;
  contentRef: RefObject<HTMLElement | null>;
}

/**
 * На статьях эксперимента шлёт Magazine / Article / Scroll при достижении
 * 25 / 50 / 75 / 100% высоты текста. Каждый порог — один раз за заход.
 */
export const useTrackCpdArticleScroll = ({ enabled, postId, abGroup, contentRef }: IUseTrackCpdArticleScrollParams) => {
  useEffect(() => {
    if (!enabled || postId === undefined) {
      return;
    }

    const sent = new Set<TArticleScrollThreshold>();
    let frameId = 0;
    let scheduled = false;

    const measure = () => {
      const element = contentRef.current;

      if (!element) {
        return;
      }

      const percent = getArticleScrollPercent(element, window.innerHeight);

      getReachedArticleScrollThresholds(percent, sent).forEach(threshold => {
        sent.add(threshold);
        caScrollCpdArticle({ scroll: threshold, abGroup, articleId: postId });
      });
    };

    const scheduleMeasure = () => {
      if (scheduled) {
        return;
      }

      scheduled = true;
      frameId = window.requestAnimationFrame(() => {
        scheduled = false;
        frameId = 0;
        measure();
      });
    };

    measure();
    window.addEventListener('scroll', scheduleMeasure, { passive: true });
    window.addEventListener('resize', scheduleMeasure);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && contentRef.current ? new ResizeObserver(scheduleMeasure) : null;

    if (resizeObserver && contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      window.removeEventListener('scroll', scheduleMeasure);
      window.removeEventListener('resize', scheduleMeasure);
      resizeObserver?.disconnect();

      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [abGroup, contentRef, enabled, postId]);
};
